import React, { memo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import AutoComplete from '@mui/material/Autocomplete';
import { CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import _ from 'lodash';

import { TextField } from 'ui/components/TextField/TextField';
import { activeUserHasPermission } from 'services/user/redux';
import { Pagination } from 'services/search';
import { initialPagination } from 'ui/components/Table/ItemsTable';
import { PaginationDirection } from 'services/url';
import { Representative } from 'services/representatives';
import { fetchRepresentativeById } from 'services/representatives/api';

import { RepresentativeAsyncAutocompleteProps } from './types';
import {
  resolvedFetchRepresentativeFunction,
  representativeAutocompleteFilterOptions,
  currencyAutocompleteOptionLabel,
} from './helpers';
import { useAsyncAutocomplete, useScrollPosition } from '../Autocomplete/hooks';
import NewRepresentativeModal from './NewRepresentativeModal';

const initialRepresentativePagination: Pagination = {
  ...initialPagination,
  pageSize: 50,
  sort: { sortBy: 'dateCreated', direction: PaginationDirection.Ascending },
};

const RepresentativeAsyncAutocomplete: React.FC<
  RepresentativeAsyncAutocompleteProps
> = (props) => {
  const {
    onChange,
    dataQa,
    permissions = [],
    disabled,
    label,
    value,
    placeholder,
    error,
    disableAdd = false,
    required,
    additionalInputProps,
    expands,
    customQuickSearchColumns,
    helperText,
  } = props;

  const {
    data: representatives,
    isLoading,
    innerValue,
    open,
    onCloseAutocomplete,
    onNextPage,
    onOpenAutocomplete,
    setSearchValue,
    setInnerValue,
    setData,
  } = useAsyncAutocomplete(
    value,
    resolvedFetchRepresentativeFunction(expands, customQuickSearchColumns),
    fetchRepresentativeById,
    initialRepresentativePagination
  );

  const [showNewRepresentativeModal, setShowNewRepresentativeModal] =
    useState(false);
  const [newItemName, setNewItemName] = useState<string | null>(null);

  const canEdit = useSelector(activeUserHasPermission(permissions));
  const { listRef, captureScrollPosition } = useScrollPosition(representatives);

  const isDisabled = disabled || !canEdit;

  const handleAutocompleteInputChange = (
    e: any,
    val: string,
    reason: string
  ) => {
    // reason why this event is triggered
    // it can be 'input', 'clear' and 'reset'
    if (reason === 'input') {
      if (!val) {
        onOpenAutocomplete();
        setInnerValue(null);
      }
      setSearchValue(val || null);
      onOpenAutocomplete();
      return;
    }

    if (reason === 'clear') {
      onChange(null);
    }

    setSearchValue(null);
  };

  const handleChange = (e: any, representative: Representative | null) => {
    if (
      representative &&
      (representative.id === null || representative.id < 0)
    ) {
      setNewItemName(representative.firstName);
      setShowNewRepresentativeModal(true);
      return;
    }

    setInnerValue(representative);
    onChange(representative);
  };

  const handleSaveNewRepresentativeModal = useCallback(
    (representative: Representative) => {
      setShowNewRepresentativeModal(false);
      setData([representative]);
      setInnerValue(representative);
      onChange(representative);
    },
    [onChange, setData, setInnerValue]
  );

  const handleCloseNewRepresentativeModal = useCallback(() => {
    setShowNewRepresentativeModal(false);
  }, []);

  return (
    <>
      <AutoComplete
        className="redesign"
        // TODO : RESTYLING : minor style adjustment needed for now until we use the FBOAutoSelect component
        style={{ marginTop: '0px', marginBottom: '0px' }}
        options={representatives}
        open={open}
        disabled={isDisabled}
        value={innerValue}
        fullWidth
        onOpen={onOpenAutocomplete}
        onClose={onCloseAutocomplete}
        onChange={handleChange}
        onInputChange={handleAutocompleteInputChange}
        getOptionLabel={currencyAutocompleteOptionLabel}
        filterOptions={representativeAutocompleteFilterOptions(disableAdd)}
        noOptionsText={isLoading ? 'Loading...' : 'No options'}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        ListboxProps={{
          onScroll: (event: React.SyntheticEvent) => {
            const node = event.currentTarget;

            if (
              Math.ceil(node.scrollTop + node.clientHeight) ===
              node.scrollHeight
            ) {
              onNextPage();
            }
          },
          // @ts-ignore
          ref: listRef,
          onScrollCapture: captureScrollPosition,
        }}
        renderInput={(params) => {
          const endAdornment = params.InputProps.endAdornment as any;

          return (
            <TextField
              // we are omitting props that would overwrite our styling in TextField
              {..._.omit(params, 'variant', 'size', 'InputLabelProps')}
              className="redesign"
              label={label}
              placeholder={placeholder}
              error={error}
              required={required}
              helperText={helperText}
              InputProps={{
                ...params.InputProps,
                ...additionalInputProps,
                endAdornment: (
                  <React.Fragment>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {endAdornment}
                  </React.Fragment>
                ),
              }}
              inputProps={{
                'data-qa': dataQa,
                'data-lpignore': 'true',
                ...params.inputProps,
              }}
            />
          );
        }}
        clearIcon={
          dataQa && (
            <CloseIcon
              fontSize="small"
              data-qa={`${dataQa}-autocomplete-clear-icon`}
            />
          )
        }
        popupIcon={
          dataQa && (
            <ArrowDropDownIcon
              fontSize="small"
              data-qa={`${dataQa}-autocomplete-dropdown-icon`}
            />
          )
        }
      />
      <NewRepresentativeModal
        show={showNewRepresentativeModal}
        onSave={handleSaveNewRepresentativeModal}
        onClose={handleCloseNewRepresentativeModal}
        newItemName={newItemName}
      />
    </>
  );
};

export default memo(RepresentativeAsyncAutocomplete);
